import React, { useContext, useEffect } from 'react'
import {
  NavLink,
  useLocation,
  useHistory,
  useRouteMatch,
} from 'react-router-dom'
import { Nav } from 'react-bootstrap'
import { matchPath } from 'react-router'
import classnames from 'classnames'
import { useUserService, SidebarDropdown } from '@abroad/components'
import { LayoutContext } from '../../utils/contexts'
import CONSTANTS from '../../utils/constants'
import SidebarAccountFooter from './SidebarAccountFooter'

const { ADMIN_ROUTES, COACH_ROUTES } = CONSTANTS

const resolveActivePaths = (linkTo, hasCoachAccess) => {
  if (hasCoachAccess && COACH_ROUTES[linkTo]) {
    return COACH_ROUTES[linkTo]
  }
  if (ADMIN_ROUTES[linkTo]) {
    return ADMIN_ROUTES[linkTo]
  }
  // Safe fallback — never call Object.values on undefined
  return [linkTo]
}

const Sidebar = () => {
  const { pathname } = useLocation()
  const { path } = useRouteMatch()
  const history = useHistory()
  const { showSidebar, showPlayBar, setShowSidebar } = useContext(LayoutContext)
  const { user } = useUserService()

  const SIDEBARLINKS_BEFORE_DROPDOWN = [
    ...(user?.isAdmin ? [{ to: '/admin/home', name: 'Home' }] : []),
    ...(user?.isCoach ? [{ to: '/coach/clients', name: 'My Clients' }] : []),
    ...(user?.isCoach ? [{ to: '/coach/scheduling', name: 'Scheduling' }] : []),
    ...(user?.isAdmin
      ? [
          { to: '/admin/organizations', name: 'Organizations' },
          { to: '/admin/networks', name: 'Networks' },
          { to: '/admin/groups', name: 'Groups' },
          { to: '/admin/users', name: 'Users' },
          { to: '/admin/coaches', name: 'Coaches' },
          { to: '/admin/experiences', name: 'Experiences' },
        ]
      : []),
  ]

  const DROPDOWN_LINKS_FOR_REPORTS = [
    ...(user?.isAdmin
      ? [
          { to: '/admin/reports', name: 'Reports' },
          { to: '/admin/subscription-payments', name: 'Subscriptions' },
          { to: '/admin/account', name: 'Accounts' },
          { to: '/admin/payment', name: 'All Payments' },
          { to: '/admin/scheduled-sessions', name: 'Scheduled Sessions' },
          { to: '/admin/scheduled-emails', name: 'Scheduled Emails' },
          { to: '/admin/session-insights', name: 'Session Insights' },
          {
            to: '/admin/statistics',
            name: 'Statistics',
            urlWords: ['statistics'],
          },
        ]
      : []),
  ]

  const DROPDOWN_LINKS_FOR_SETTINGS = [
    ...(user?.isAdmin
      ? [
          { to: '/admin/promo', name: 'Promo Codes', urlWords: ['promo'] },
          {
            to: '/admin/experiences-media',
            name: 'Experiences Medias',
            urlWords: ['blogs'],
          },
        ]
      : []),
  ]

  // Close the mobile drawer after navigation. Use a live width check — the
  // module-level `isLGScreen` in utility.js is frozen at first import and would
  // keep closing the desktop gnav after resizing up from a small viewport.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      setShowSidebar(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <aside id='sidebar' className='position-relative experience-sidebar'>
      <Nav
        className={classnames(
          'flex-column flex-nowrap vh-100 sidebar-wrapper space-y-2',
          {
            hide: !showSidebar,
            'sidebar-wrapper-with-play-bar': showPlayBar,
          },
        )}>
        <div
          className='experience-mark'
          onClick={() => history.push('/admin/home')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              history.push('/admin/home')
            }
          }}
          role='button'
          tabIndex={0}>
          Abroad
        </div>
        <p className='experience-nav-caption'>
          <a
            href={`${process.env.REACT_APP_ALTERNATE_DOMAIN}/home`}
            className='experience-nav-caption-link'>
            Client Platform
          </a>
        </p>
        <div className='space-y-4 sidebar-section pb-5 mb-3 pb-lg-2 mb-lg-0'>
          <div className='space-y-3'>
            {SIDEBARLINKS_BEFORE_DROPDOWN.map((link) => (
              <NavItem
                key={link.name}
                link={link}
                hasCoachAccess={user?.isCoach}
              />
            ))}
            {user?.isAdmin && (
              <>
                <SidebarDropdown
                  links={DROPDOWN_LINKS_FOR_REPORTS}
                  name='Analytics'
                  path={path}
                  pathname={pathname}
                  NavLink={NavLink}
                  isRenderOnClientSide={false}
                />
                <SidebarDropdown
                  links={DROPDOWN_LINKS_FOR_SETTINGS}
                  name='Settings'
                  path={path}
                  pathname={pathname}
                  NavLink={NavLink}
                  isRenderOnClientSide={false}
                />
              </>
            )}
          </div>
        </div>
        <SidebarAccountFooter />
      </Nav>
    </aside>
  )
}

const NavItem = ({ link, hasCoachAccess }) => {
  return (
    <Nav.Item>
      <Nav.Link
        isActive={(match, location) => {
          const locationPathName = location?.pathname
          const paths = resolveActivePaths(link.to, hasCoachAccess)
          return paths.some(
            (routePath) =>
              matchPath(locationPathName, {
                path: routePath,
                exact: true,
                strict: false,
              }) !== null,
          )
        }}
        as={NavLink}
        to={link.to}>
        {link?.icon ? (
          <span className='d-flex align-items-center'>
            <span className='nav-link-icon'>{link.icon}</span>
            {link.name}
          </span>
        ) : (
          link.name
        )}
      </Nav.Link>
    </Nav.Item>
  )
}

export default Sidebar
